import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ToolTip from "components/design/toolTip";
import { OfficeIcon } from "components/icons/Office";
import { ShieldStar } from "components/icons/ShieldStar";
import { DASHBOARD_STATUS } from "constants/task";
import dayjs from "dayjs";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  StateRegistrationsRes,
  StateRegistrationStatusFilter,
} from "store/apis/StateRegistrations";
import { RegistrationStatusName } from "store/apis/registrations";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { pluralize } from "utils/pluralize";
import { StateColumn } from "./StateColumn";

type DeadlineCellProps = {
  deadlineDate: string | null;
  isTaskFinal: boolean | undefined;
  isOverdue: boolean;
  isUrgent: boolean;
  daysLeft: number | null;
};

const getDeadlineStatusConfig = ({
  isTaskFinal,
  isOverdue,
  isUrgent,
  daysLeft,
}: Omit<DeadlineCellProps, "deadlineDate">): {
  dotClassName: string;
  label: string;
} | null => {
  if (isTaskFinal) return null;
  if (isOverdue)
    return {
      dotClassName: "t-bg-red-50",
      label: `Overdue by ${pluralize(Math.abs(daysLeft ?? 0), "day", "days")}`,
    };
  if (isUrgent)
    return {
      dotClassName: daysLeft === 0 ? "t-bg-red-50" : "t-bg-yellow-50",
      label:
        daysLeft === 0
          ? "Due today"
          : `Due in ${pluralize(daysLeft ?? 0, "day", "days")}`,
    };
  return null;
};

const DeadlineCell = ({
  deadlineDate,
  isTaskFinal,
  isOverdue,
  isUrgent,
  daysLeft,
}: DeadlineCellProps) => {
  if (!deadlineDate) {
    return <span className="t-text-body-sm t-text-text-30">—</span>;
  }

  const statusConfig = getDeadlineStatusConfig({
    isTaskFinal,
    isOverdue,
    isUrgent,
    daysLeft,
  });

  return (
    <div className="t-flex t-flex-col t-gap-0.5 t-w-fit">
      <span className="t-text-body-sm t-font-medium t-text-text-100">
        {deadlineDate}
      </span>
      {statusConfig && (
        <span className="t-flex t-items-center t-gap-1.5 t-text-caption t-text-text-30">
          <span
            className={`t-w-1.5 t-h-1.5 t-rounded-full ${statusConfig.dotClassName}`}
          />
          {statusConfig.label}
        </span>
      )}
    </div>
  );
};

type StateTableRowProps = {
  row: StateRegistrationsRes;
  nextDeadlineTask: TaskDeadlineOverviewItem | undefined;
  onRowClick: () => void;
  onLetInkleHandle: () => void;
  isLetInkleHandleLoading?: boolean;
  onDeadlineClick: (task: TaskDeadlineOverviewItem) => void;
  onStartFiling: (task: TaskDeadlineOverviewItem) => void;
  isStartingFiling: boolean;
  onAddMissingInfo: (row: StateRegistrationsRes) => void;
  onStartRegistration: (stateId: string) => void;
  onViewCart: () => void;
  isMobile?: boolean;
};

// Matches the State / Filing deadline / Status / RA Status / Action columns.
const MOBILE_COL_SPAN = 5;

export const StateTableRow = ({
  row,
  nextDeadlineTask,
  onRowClick,
  onLetInkleHandle,
  isLetInkleHandleLoading = false,
  onDeadlineClick,
  onStartFiling,
  isStartingFiling,
  onAddMissingInfo,
  onStartRegistration,
  onViewCart,
  isMobile = false,
}: StateTableRowProps) => {
  const history = useHistory();
  const isWithdrawn = Boolean(row.withdrawal_date);

  const deadlineDate = row.deadline_date
    ? dayjs(row.deadline_date).format("MMM D, YYYY")
    : null;

  const daysLeft = row.deadline_date
    ? dayjs(row.deadline_date).diff(dayjs(), "day")
    : null;

  const isTaskFinal =
    nextDeadlineTask &&
    "is_final" in nextDeadlineTask.current_state &&
    nextDeadlineTask.current_state.is_final;

  const isOverdue = row.is_deadline_overdue;
  const isUrgent = row.is_deadline_due;

  const isOngoing =
    nextDeadlineTask?.dashboard_status === DASHBOARD_STATUS.ONGOING &&
    !isTaskFinal;

  const rowStatuses = row.compliance_status.statuses;
  const sosIsDone = row.compliance_status.is_sos_done;
  const raIsInkle = sosIsDone && Boolean(row.REGISTERED_AGENT.is_inkle_ra);

  const isNotRegistered = rowStatuses.includes(
    StateRegistrationStatusFilter.NOT_REGISTERED
  );
  const isFilingInProgress = rowStatuses.includes(
    StateRegistrationStatusFilter.FILING_IN_PROGRESS
  );
  const rowHasMissingInfo = rowStatuses.includes(
    StateRegistrationStatusFilter.MISSING_INFO
  );
  const rowMissingFieldLabels = row.compliance_status.missing_field_labels;

  const hasFqPending =
    !isWithdrawn &&
    !row.is_home_state &&
    row.has_sos_intake &&
    Boolean(row.SECRETARY_OF_STATE.cart_status);

  const isUnstarted =
    !isWithdrawn &&
    !sosIsDone &&
    !hasFqPending &&
    !rowHasMissingInfo &&
    row.SECRETARY_OF_STATE.status === RegistrationStatusName.NOT_REGISTERED &&
    !row.has_sos_intake;

  const viewDetailsAction = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    onRowClick();
  };

  const addMissingInfoAction = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    onAddMissingInfo(row);
  };

  const handleStartRegistrationClick = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    onStartRegistration(row.state_id);
  };

  const handleViewCartClick = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    onViewCart();
  };

  let filingActionLabel: string = "View details";
  let filingAction: (e?: React.MouseEvent) => void = viewDetailsAction;

  const handleViewFilingClick = (e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    history.push(`/tax/filings/${row.deadline_task_uuid}`);
  };

  if (isWithdrawn) {
    filingActionLabel = "View details";
    filingAction = viewDetailsAction;
  } else if (isUnstarted) {
    filingActionLabel = "Get started";
    filingAction = handleStartRegistrationClick;
  } else if (rowHasMissingInfo) {
    filingActionLabel = "Complete info";
    filingAction = addMissingInfoAction;
  } else if (row.deadline_task_uuid) {
    filingActionLabel = "View filing";
    filingAction = handleViewFilingClick;
  } else if (!nextDeadlineTask) {
    filingActionLabel = "View details";
    filingAction = viewDetailsAction;
  } else if (isTaskFinal) {
    filingActionLabel = "View details";
    filingAction = viewDetailsAction;
  } else if (nextDeadlineTask.task_uuid || isOngoing) {
    filingActionLabel = "View filing";
    filingAction = (e?: React.MouseEvent) => {
      e?.stopPropagation?.();
      onDeadlineClick(nextDeadlineTask);
    };
  } else {
    filingActionLabel = "Start filing";
    filingAction = (e?: React.MouseEvent) => {
      e?.stopPropagation?.();
      onStartFiling(nextDeadlineTask);
    };
  }

  if (hasFqPending && !isFilingInProgress && !rowHasMissingInfo) {
    filingActionLabel = "View cart";
    filingAction = handleViewCartClick;
  }

  const handleLetInkleHandleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLetInkleHandle();
  };

  const handleFilingActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    filingAction();
  };

  const hasStatusTags =
    isNotRegistered || rowHasMissingInfo || hasFqPending || isFilingInProgress;

  const isRegistered = rowStatuses.includes(
    StateRegistrationStatusFilter.REGISTERED
  );
  const doneStatusLabel = isRegistered ? "Registered" : null;

  const stateCell = (
    <>
      <StateColumn
        stateDetail={row.state}
        isHomeState={row.is_home_state}
        withdrawalDate={row.withdrawal_date}
      />
    </>
  );

  const deadlineCell = (
    <>
      <DeadlineCell
        deadlineDate={deadlineDate}
        isTaskFinal={false}
        isOverdue={isOverdue}
        isUrgent={isUrgent}
        daysLeft={daysLeft}
      />
    </>
  );

  const statusCell = (
    <>
      {hasStatusTags || doneStatusLabel ? (
        <div className="t-flex t-items-center t-gap-1.5">
          {doneStatusLabel && !rowHasMissingInfo && (
            <Tag tagType="green" size="small" icon={false} rounded>
              {doneStatusLabel}
            </Tag>
          )}
          {isNotRegistered && !isFilingInProgress && !rowHasMissingInfo && (
            <Tag tagType="red" size="small" icon={false} rounded>
              Not registered
            </Tag>
          )}
          {isFilingInProgress && (
            <Tag tagType="orange" size="small" icon={false} rounded>
              Filing in progress
            </Tag>
          )}
          {rowHasMissingInfo && (
            <ToolTip
              text={
                <div className="t-flex t-flex-col t-gap-2 t-text-left">
                  <span className="t-text-body-sm t-font-semibold">
                    Missing info
                  </span>
                  <ul className="t-m-0 t-list-none t-p-0 t-flex t-flex-col t-gap-0.5">
                    {rowMissingFieldLabels.map((label) => (
                      <li key={label} className="t-text-body-sm">
                        {label}
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    customType="secondary"
                    size="xsmall"
                    onClick={addMissingInfoAction}
                  >
                    Add info
                  </Button>
                </div>
              }
              disableHoverableContent={false}
              propClass="t-w-60"
              side="bottom"
            >
              <span>
                <Tag tagType="orange" size="small" icon={false} rounded>
                  Missing info
                </Tag>
              </span>
            </ToolTip>
          )}
        </div>
      ) : (
        <span className="t-text-body-sm t-text-text-30">—</span>
      )}
    </>
  );

  const raCell = (
    <>
      {sosIsDone ? (
        <div className="t-flex t-flex-col t-gap-1">
          {raIsInkle ? (
            <div className="t-flex t-flex-col t-gap-0.5">
              <span className="t-inline-flex t-w-fit t-items-center t-gap-1.5 t-text-body-sm t-text-text-60 [&_svg]:t-h-3.5 [&_svg]:t-w-3.5">
                <ShieldStar />
                <span> Inkle RA</span>
              </span>
              <span className="t-pl-5 t-text-caption t-text-text-60">
                Deadlines tracked automatically
              </span>
            </div>
          ) : (
            <>
              <span className="t-inline-flex t-w-max t-items-center t-gap-1.5 t-text-body-sm t-text-text-60 [&_svg]:t-h-3 [&_svg]:t-w-3 [&_svg_path]:t-fill-neutral-40">
                <OfficeIcon />
                External RA
              </span>
              <span className="t-pl-5">
                <Button
                  customType="link"
                  size="xsmall"
                  className="!t-text-caption"
                  onClick={handleLetInkleHandleClick}
                  isLoading={isLetInkleHandleLoading}
                  disabled={isLetInkleHandleLoading}
                >
                  Move to Inkle →
                </Button>
              </span>
            </>
          )}
        </div>
      ) : (
        <span className="t-text-body-sm t-text-text-30">—</span>
      )}
    </>
  );

  const actionCell = (
    <>
      <Button
        customType="secondary"
        size="small"
        onClick={handleFilingActionClick}
        isLoading={filingActionLabel === "Start filing" && isStartingFiling}
        disabled={filingActionLabel === "Start filing" && isStartingFiling}
      >
        {filingActionLabel}
      </Button>
    </>
  );

  if (isMobile) {
    const mobileRaCell = sosIsDone && (
      <div className="t-flex t-flex-wrap t-items-center t-gap-x-2 t-gap-y-0.5 t-text-body-sm t-text-text-60">
        {raIsInkle ? (
          <span className="t-inline-flex t-items-center t-gap-1.5 [&_svg]:t-h-3.5 [&_svg]:t-w-3.5">
            <ShieldStar />
            Inkle RA
          </span>
        ) : (
          <>
            <span className="t-inline-flex t-items-center t-gap-1.5 [&_svg]:t-h-3 [&_svg]:t-w-3 [&_svg_path]:t-fill-neutral-40">
              <OfficeIcon />
              External RA
            </span>
            <Button
              customType="link"
              size="xsmall"
              className="!t-text-caption"
              onClick={handleLetInkleHandleClick}
              isLoading={isLetInkleHandleLoading}
              disabled={isLetInkleHandleLoading}
            >
              Move to Inkle →
            </Button>
          </>
        )}
      </div>
    );

    return (
      <Table.Row onRowClick={onRowClick} className="last:t-border-b-0">
        <Table.Cell colSpan={MOBILE_COL_SPAN} className="!t-px-0 !t-py-3">
          <div className="t-flex t-flex-col t-gap-2">
            <div className="t-flex t-items-start t-justify-between t-gap-2">
              <div className="t-min-w-0 t-flex-1">{stateCell}</div>
              <div className="t-shrink-0">{actionCell}</div>
            </div>
            <div className="t-flex t-flex-wrap t-items-center t-gap-x-3 t-gap-y-1">
              {statusCell}
              {deadlineDate && deadlineCell}
            </div>
            {mobileRaCell}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <Table.Row onRowClick={onRowClick}>
      <Table.Cell>{stateCell}</Table.Cell>

      <Table.Cell className="t-w-1/4">{deadlineCell}</Table.Cell>

      <Table.Cell>{statusCell}</Table.Cell>

      <Table.Cell>{raCell}</Table.Cell>

      <Table.Cell>{actionCell}</Table.Cell>
    </Table.Row>
  );
};
